import { SourceTypes } from '../../../../constants';
import { buildProxyURL, formatText, handleResponseError } from '../../utils';
import type { DataValue, ProviderType } from '../types';
import type {
  FacebookResponseMediaItem,
  FacebookResponseType,
  FacebookSourceValue
} from './types';
import { getFacebookMedia } from './utils';

const PLATFORM = SourceTypes.FACEBOOK;

const attachmentsFields = 'media,media_type,type,title,description,url';
const REQUEST_FIELDS = [
  'created_time',
  'permalink_url',
  'from',
  'message',
  'full_picture',
  'comments.summary(true){from,created_time,message,id}',
  'likes.summary(true){name,id}',
  `attachments{${attachmentsFields},subattachments{${attachmentsFields}}}`
];

const select = (response: FacebookResponseType): DataValue[] => {
  handleResponseError(response, PLATFORM, 'data');

  return response.data.reduce<DataValue[]>(
    (
      acc,
      {
        id,
        message,
        from,
        likes,
        attachments,
        full_picture: fullPicture,
        permalink_url: permalinkUrl,
        created_time: createdTime,
        comments: { summary: commentsSummary }
      }
    ) => {
      const text = formatText(message, PLATFORM);
      const attachment: Partial<FacebookResponseMediaItem> =
        attachments?.data?.[0] ?? {};

      if (fullPicture && attachment?.media?.image) {
        attachment.media.image.src = fullPicture;
      }

      const media = getFacebookMedia(attachment);

      if (text || media?.src || media?.thumbnail) {
        acc.push({
          id,
          text,
          media,
          source: {
            type: PLATFORM,
            url: permalinkUrl
          },
          timestamp: Date.parse(createdTime),
          user: {
            name: from.name,
            image: `https://graph.facebook.com/${from.id}/picture?type=square`,
            url: `https://facebook.com/${from.id}`
          },
          counters: {
            likes: likes?.summary?.total_count || 0,
            comments: commentsSummary.total_count
          }
        });
      }

      return acc;
    },
    []
  );
};

export function createFacebookPageProvider(
  sourceValue: FacebookSourceValue,
  globalKey: string[],
  filtersKey: string[]
): ProviderType<FacebookResponseType> {
  const { source, _customFetchLimit } = sourceValue;
  const { data: selectedPage, identity = '' } = source ?? {};
  const id = selectedPage?.id ?? '';

  const key = [...globalKey, ...filtersKey, id, identity];

  const page = (data?: FacebookResponseType) => {
    if (data) {
      const nextLink = data.paging?.next;
      if (!nextLink) {
        return;
      }

      const { search } = new URL(nextLink);
      const requestUrlQuery = `${id}/posts${search}`;

      return buildProxyURL({
        apiSource: PLATFORM,
        params: { q: requestUrlQuery, user_id: identity }
      });
    }

    const requestParams = new URLSearchParams({
      fields: REQUEST_FIELDS.join(',')
    });

    if (_customFetchLimit) {
      requestParams.append('limit', `${_customFetchLimit}`);
    }

    const requestUrlQuery = `${id}/posts?${requestParams}`;

    return buildProxyURL({
      apiSource: PLATFORM,
      params: { q: requestUrlQuery, user_id: identity }
    });
  };

  return { key, page, select };
}
